import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { googleAuthService } from '../../services/auth/googleAuthService';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { error } = useAuth();
  const [selectedRole, setSelectedRole] = useState<'student' | 'teacher' | null>(null);

  // Reset khi modal đóng
  useEffect(() => {
    if (isOpen) setSelectedRole(null);
  }, [isOpen]);

  // Render Google Sign-In button sau khi chọn role
  useEffect(() => {
    if (isOpen && selectedRole) {
      googleAuthService.setLoginContext(selectedRole, 'ebook');
      const timer = setTimeout(() => {
        googleAuthService.initialize().then(() => {
          googleAuthService.renderSignInButton('google-signin-button');
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, selectedRole]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Đăng nhập</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {/* Bước 1: Chọn role */}
          {!selectedRole && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 text-center mb-4">
                Bạn là Giáo viên hay Học sinh?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setSelectedRole('teacher')}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-500 transition-colors text-2xl">
                    👨‍🏫
                  </div>
                  <span className="font-bold text-sm text-gray-800">Giáo viên</span>
                </button>
                <button
                  onClick={() => setSelectedRole('student')}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all group"
                >
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-500 transition-colors text-2xl">
                    🎓
                  </div>
                  <span className="font-bold text-sm text-gray-800">Học sinh</span>
                </button>
              </div>
            </div>
          )}

          {/* Bước 2: Sau khi chọn role — hiện badge + Google button */}
          {selectedRole && (
            <>
              <div className="flex items-center justify-center gap-2 mb-5 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-full">
                <span>{selectedRole === 'teacher' ? '👨‍🏫 Giáo viên' : '🎓 Học sinh'}</span>
                <button
                  onClick={() => setSelectedRole(null)}
                  className="text-blue-500 hover:text-blue-700 underline"
                >
                  Đổi
                </button>
              </div>

              <div className="h-[44px] flex justify-center">
                <div id="google-signin-button" className="w-full flex justify-center" />
              </div>
            </>
          )}

          <p className="mt-6 text-center text-xs text-gray-400">
            Nền tảng Vật Lý 365 dành cho{' '}
            <span className="font-semibold text-blue-600">Giáo viên</span> và{' '}
            <span className="font-semibold text-green-600">Học sinh</span>.
          </p>
        </div>
      </div>

      {/* Click outside to close */}
      <div className="absolute inset-0 z-[-1]" onClick={onClose} />
    </div>
  );
};

export default LoginModal;
